// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';
import { css } from '@emotion/react';

import {
  DateTime,
  formatToDateTime,
  formatToTimeShort,
  getDateTime,
} from 'infras/datetime';
import {
  spacing,
  fontSize,
  fontWeight,
  zIndex,
  borderRadius,
  mediaMax,
} from 'presentations/views/styles';
import {
  black,
  blueGrayPale,
  gray03,
  gray04,
  secondary,
} from 'presentations/views/styles/color';
import Dialog from 'presentations/views/components/atoms/Dialog';
import Icon from 'presentations/views/components/atoms/Icon';
import IconButton from 'presentations/views/components/atoms/IconButton';
import { EventTypeRestrictServiceUsageItem } from 'presentations/api/schema/event_type';

interface Props {
  restrictServiceUsages: EventTypeRestrictServiceUsageItem[] | null;
  isOpen: boolean;
  onNext?: Function;
  onClose?: Function;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);

  const services: {
    usage: EventTypeRestrictServiceUsageItem;
    summaryText: string[];
    exceedMessage: string;
  }[] = [];
  (props.restrictServiceUsages ?? []).forEach((usage) => {
    const summaryText = [];
    const exceedRanges = [];
    if (
      usage.isEnableUserMaxDailyUsesCount &&
      usage.userMaxDailyUsesCount &&
      usage.userCurrentDailyUsesCount >= usage.userMaxDailyUsesCount
    ) {
      summaryText.push(
        `${t('day')} : ${usage.userCurrentDailyUsesCount} / ${
          usage.userMaxDailyUsesCount
        }${t('times')}`,
      );
      exceedRanges.push(t('day'));
    }
    if (
      usage.isEnableUserMaxWeeklyUsesCount &&
      usage.userMaxWeeklyUsesCount &&
      usage.userCurrentWeeklyUsesCount >= usage.userMaxWeeklyUsesCount
    ) {
      summaryText.push(
        `${t('week')} : ${usage.userCurrentWeeklyUsesCount} / ${
          usage.userMaxWeeklyUsesCount
        }${t('times')}`,
      );
      exceedRanges.push(t('week'));
    }
    if (
      usage.isEnableUserMaxMonthlyUsesCount &&
      usage.userMaxMonthlyUsesCount &&
      usage.userCurrentMonthlyUsesCount >= usage.userMaxMonthlyUsesCount
    ) {
      summaryText.push(
        `${t('month')} : ${usage.userCurrentMonthlyUsesCount} / ${
          usage.userMaxMonthlyUsesCount
        }${t('times')}`,
      );
      exceedRanges.push(t('month'));
    }

    if (summaryText.length > 0) {
      services.push({
        usage,
        summaryText,
        exceedMessage: exceedRanges.join('、'),
      });
    }
  });

  return (
    <Dialog
      isOpen={props.isOpen}
      titleComponent={
        <TitleContainer>
          {t('title')}
          <CloseIconButton aria-label="close" onClick={props.onClose}>
            <CloseIcon name="close" size={fontSize(28)} />
          </CloseIconButton>
        </TitleContainer>
      }
      onClose={props.onClose}
      scroll
      maxHeight="50vh"
      mobileMaxHeight="100%"
      zIndex={zIndex(1500)}
    >
      <Content>
        <DescriptionText>{t('description.0')}</DescriptionText>
        <DescriptionText>{t('description.1')}</DescriptionText>
        <ServiceContainer>
          <SectionTitle>{t('usage description')}</SectionTitle>
          {services.map((service) => {
            const { usage, summaryText, exceedMessage } = service;
            return (
              <ServiceItem>
                <ServiceTitle>{usage.name}</ServiceTitle>
                <ServiceUsage>
                  {summaryText.map((summary) => (
                    <UsageSummary>{summary}</UsageSummary>
                  ))}
                </ServiceUsage>
                <ServiceRestriction>
                  {t('exceedMessage', { exceedMessage })}
                </ServiceRestriction>
              </ServiceItem>
            );
          })}
        </ServiceContainer>
      </Content>
    </Dialog>
  );
}

const TitleContainer = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: ${spacing(16)};
  line-height: 44px;
  vertical-align: middle;
`;
const CloseIconButton = styled(IconButton)`
  position: absolute;
  right: 0;
  color: ${gray03};
`;
const CloseIcon = styled(Icon)``;

const Content = styled.div``;
const DescriptionText = styled.div`
  font-size: ${fontSize(14)};
  // width: 400px;
`;

const ServiceContainer = styled.div`
  padding: ${spacing(24)};
  border-radius: ${borderRadius(12)};
  margin-top: ${spacing(24)};
  margin-bottom: ${spacing(24)};
  background-color: ${blueGrayPale};
`;

const SectionTitle = styled.div`
  font-size: ${fontSize(16)};
  font-weight: ${fontWeight('bold')};
  color: ${black};
`;

const ServiceItem = styled.div`
  margin: ${spacing(24)} 0 ${spacing(24)} 0;

  border-bottom: 1px solid ${gray04};
  padding-bottom: ${spacing(24)};

  &:last-of-type {
    border-bottom: 0px;
    margin-bottom: 0px;
    padding-bottom: 0px;
  }
`;

const ServiceTitle = styled.div`
  font-size: ${fontSize(14)};
  font-weight: ${fontWeight('bold')};
  margin-bottom: ${spacing(8)};
`;
const ServiceUsage = styled.div`
  display: flex;
  font-size: ${fontSize(14)};
  margin-bottom: ${spacing(8)};
`;
const ServiceRestriction = styled.div`
  font-size: ${fontSize(14)};
  color: ${secondary};
`;
const UsageSummary = styled.div`
  margin-right: ${spacing(24)};
`;
